import { Group, OrganisationResponse } from 'api';
import Box from '@mui/material/Box';
import { styled, SxProps, Theme } from '@mui/material';
import {
  InputText,
  InputType,
  TextV2,
} from '@insights-ltd/design-library/components';
import { useTranslation } from 'react-i18next';
import Autocomplete, {
  AutocompleteChangeReason,
  AutocompleteRenderInputParams,
  createFilterOptions,
} from '@mui/material/Autocomplete';
import DisplayOrgAssign from './SelectGroupDisplayOrgAssigned';

const StyledInput = styled(InputText)(({ theme }) => ({
  '& > div': {
    backgroundColor: theme.palette.background.paper,
    height: '3rem',
    width: '100%',
  },
}));

const ErrorMessage = styled('p')(({ theme }) => ({
  ...theme.typography.caption,
  color: `${theme.palette.error.main}`,
  margin: '3px 14px 0 14px',
}));

type GroupAutoCompleteProps = {
  onSelect: (value: Group | null) => void;
  options: Group[];
  loading?: boolean;
  helperText?: string | null;
  required?: boolean;
  error?: boolean;
  selectedValue?: string | null;
  fieldLabel?: string | React.ReactNode;
};
const GroupAutoComplete = ({
  options,
  selectedValue,
  onSelect,
  loading,
  helperText,
  required,
  error,
  fieldLabel,
}: GroupAutoCompleteProps) => {
  const { t } = useTranslation();
  const getSelectedItem = () =>
    options?.length && selectedValue
      ? (options.find(({ id }) => id === selectedValue) as Group)
      : null;

  return (
    <>
      <Autocomplete
        id="group-search"
        data-testid="group-autocomplete"
        options={options}
        fullWidth
        noOptionsText={t(
          'ui.event-management.organisations.add.to-group.no-options-text',
        )}
        isOptionEqualToValue={(option, val) => option.id === val.id}
        onChange={(_, changeValue, reason) => {
          if (
            reason === ('selectOption' as AutocompleteChangeReason) &&
            changeValue !== null
          ) {
            onSelect(changeValue);
          }
        }}
        filterOptions={createFilterOptions({
          stringify: (option: Group) => `${option.name}`,
        })}
        getOptionLabel={(option) => option.name || ''}
        renderOption={(fullProps, val) => {
          const { color, ...rest } = fullProps;
          return (
            <li {...rest} key={val.id} data-testid={`group-option-${val.id}`}>
              <TextV2 component="span" variant="bodyBold" color="primary">
                {val.name}
              </TextV2>
            </li>
          );
        }}
        value={getSelectedItem()}
        loading={loading}
        renderInput={(params: AutocompleteRenderInputParams) => {
          const { size, id, ...rest } = params;
          const { ref } = rest.slotProps.input;
          const { children, ...props } = rest.slotProps.htmlInput;
          return (
            <div ref={ref}>
              <StyledInput
                data-testid="group-autocomplete-text-field"
                id={id}
                {...props}
                label={fieldLabel}
                placeholder={t(
                  'ui.event-management.organisations.add.select-group-placeholder',
                )}
                required={!!required}
                error={error}
                type={'text' as InputType}
                sx={{ with: '100%' }}
              />
            </div>
          );
        }}
        sx={{ marginTop: '0.5rem', borderColor: 'black' }}
      />
      {helperText ? <ErrorMessage>{helperText}</ErrorMessage> : null}
    </>
  );
};

type SelectGroupProps = {
  options: Group[];
  selectedGroup?: (Group & { organisations?: OrganisationResponse[] }) | null;
  onSelect: (
    group: (Group & { organisations?: OrganisationResponse[] }) | null,
  ) => void;
  sx?: SxProps<Theme>;
  selectedLabel?: string | React.ReactNode;
  fieldLabel?: string | React.ReactNode;
  error?: string | null;
  actionType?: 'remove' | 'change';
  disabled?: boolean;
};
const SelectGroup = ({
  selectedGroup = null,
  options,
  onSelect,
  sx,
  selectedLabel,
  fieldLabel,
  error,
  actionType = 'remove',
  disabled,
}: SelectGroupProps) => {
  return (
    <Box sx={sx}>
      {!disabled && selectedGroup === null ? (
        <GroupAutoComplete
          fieldLabel={fieldLabel}
          selectedValue={selectedGroup}
          options={options}
          onSelect={onSelect}
          error={!!error}
          helperText={error}
        />
      ) : (
        <DisplayOrgAssign
          {...{
            selectedGroup,
            onSelect,
            selectedLabel,
            error,
            actionType,
            disabled,
          }}
        />
      )}
    </Box>
  );
};

export default SelectGroup;
